import type { AuthUser } from "../types/profile";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaLock, FaUserPlus } from "react-icons/fa";
import defaultPhoto from "../images/default-photo.jpeg";
import { useFirebase } from "../context/FirebaseContext";
import { useI18n } from "../i18n/useI18n";
import useProfileAccess from "../hooks/useProfileAccess";
import { buildFriendRequestPayload } from "../utility/friendRequests";
import { invalidateFriendRosterCache } from "../utility/friendRosterCache";
import { invalidateCachedValue } from "../utility/localDataCache";
import { getPrivacyAccessMessage } from "../utility/profilePrivacy";
import Loader from "./InsertElements/Loader";

export const ClosedProfilePage = ({ access, viewer }: { access: ReturnType<typeof useProfileAccess>; viewer?: AuthUser | null }) => {
  const { firebase, firestore } = useFirebase();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [requestState, setRequestState] = useState("idle");
  const profile = access.publicProfile || {};
  const message = getPrivacyAccessMessage(access.privacyMode);
  const canRequestFriendship = Boolean(
    viewer?.uid && access.ownerId && viewer.uid !== access.ownerId
  );
  const canAddWithoutRequest = Boolean(
    access.ownerHasViewer && !access.viewerHasOwner
  );

  useEffect(() => {
    let active = true;
    if (!canRequestFriendship || !viewer?.uid || !access.ownerId || access.privacyMode !== "friends") return undefined;
    if (canAddWithoutRequest) {
      // The profile owner still has the viewer in their friends. Restoring
      // the viewer's own relation must not create a duplicate request or
      // notify the profile owner again.
      setRequestState("idle");
      return undefined;
    }

    const requestRef = firestore
      .collection("users")
      .doc(access.ownerId)
      .collection("friendRequests")
      .doc(viewer.uid);
    void requestRef.get({ source: "server" }).then((snapshot) => {
      if (!active || !snapshot.exists) return;
      const status: unknown = snapshot.data()?.status;
      if (status === "pending") setRequestState("sent");
    }).catch(() => {
      // The action itself still reports a useful error if the request cannot
      // be read or written. A failed status preflight must not hide the button.
    });

    return () => {
      active = false;
    };
  }, [
    access.ownerId,
    access.privacyMode,
    canAddWithoutRequest,
    canRequestFriendship,
    firestore,
    viewer?.uid
  ]);

  const sendFriendRequest = async () => {
    if (!canRequestFriendship || !viewer?.uid || !access.ownerId || requestState === "sending") return;
    setRequestState("sending");
    try {
      const timestamp = firebase.firestore.FieldValue.serverTimestamp();
      const ownFriendRef = firestore
        .collection("users")
        .doc(viewer.uid)
        .collection("friends")
        .doc(access.ownerId);
      const ownFriendPayload = {
        uid: access.ownerId,
        name: profile.name || profile.displayName || "Пользователь",
        displayName: profile.displayName || profile.name || "Пользователь",
        photoURL: profile.photoURL || profile.avatarUrl || "",
        avatarUrl: profile.avatarUrl || profile.photoURL || "",
        addedAt: timestamp
      };

      if (canAddWithoutRequest) {
        const batch = firestore.batch();
        batch.set(ownFriendRef, ownFriendPayload, { merge: true });
        await batch.commit();
        await Promise.all([
          invalidateCachedValue(`friend_relation_${viewer.uid}_${access.ownerId}`),
          invalidateFriendRosterCache(viewer.uid)
        ]);
        setRequestState("accepted");
        return;
      }

      const requestRef = firestore
        .collection("users")
        .doc(access.ownerId)
        .collection("friendRequests")
        .doc(viewer.uid);
      const existingRequest = await requestRef.get({ source: "server" }).catch(() => null);
      const existingStatus: unknown = existingRequest?.exists
        ? existingRequest.data()?.status
        : "";
      if (existingStatus === "pending") {
        setRequestState("sent");
        return;
      }
      if (existingRequest?.exists) {
        // Requesters cannot update a request already resolved by its owner.
        // Deleting their old resolved request before creating a fresh pending
        // one keeps retries valid under Firestore Rules.
        await requestRef.delete();
      }
      const batch = firestore.batch();
      batch.set(ownFriendRef, ownFriendPayload, { merge: true });
      batch.set(requestRef, buildFriendRequestPayload({
        requesterId: viewer.uid,
        recipientId: access.ownerId,
        requester: viewer,
        recipient: profile,
        timestamp
      }), { merge: true });
      await batch.commit();
      await Promise.all([
        invalidateCachedValue(`friend_relation_${viewer.uid}_${access.ownerId}`),
        invalidateFriendRosterCache(viewer.uid)
      ]);
      setRequestState("sent");
    } catch (error) {
      console.error("Не удалось отправить заявку в друзья:", error);
      setRequestState("error");
    }
  };

  return (
    <main className="closed-profile-page">
      <section className="closed-profile-card" aria-labelledby="closed-profile-title">
        <div className="closed-profile-card__lock" aria-hidden="true"><FaLock /></div>
        <img
          className="closed-profile-card__avatar"
          src={profile.avatarUrl || profile.photoURL || defaultPhoto}
          alt=""
          loading="eager"
          decoding="async"
          onError={(event) => {
            event.currentTarget.src = defaultPhoto;
          }}
        />
        <p className="closed-profile-card__name">
          {profile.name || profile.displayName || t("Пользователь")}
        </p>
        <h1 id="closed-profile-title">{t(message.title)}</h1>
        <p>{t(message.description)}</p>
        <div className="closed-profile-card__actions">
          <button type="button" onClick={() => navigate(-1)}>{t("Вернуться назад")}</button>
          {canRequestFriendship && access.privacyMode === "friends" && (
            <button
              type="button"
              onClick={sendFriendRequest}
              disabled={[
                "sending",
                "sent",
                "accepted"
              ].includes(requestState)}
            >
              <FaUserPlus aria-hidden="true" />
              {t(requestState === "accepted"
                ? "Вы уже друзья"
                : requestState === "sent"
                  ? "Заявка отправлена"
                  : "Добавить в друзья")}
            </button>
          )}
        </div>
        {requestState === "error" && (
          <p className="closed-profile-card__error" role="alert">
            {t("Не удалось отправить заявку в друзья")}
          </p>
        )}
      </section>
    </main>
  );
};

const ProfileAccessBoundary = ({ ownerId, viewer, children }: { ownerId?: string | null; viewer?: AuthUser | null; children: (access: ReturnType<typeof useProfileAccess>) => React.ReactNode }) => {
  const { t } = useI18n();
  const access = useProfileAccess({ ownerId, viewer });

  if (access.loading) {
    return (
      <div className="page-loader" role="status" aria-live="polite">
        <div className="page-loader__content">
          <Loader />
          <span>{t("Проверяем доступ к профилю...")}</span>
        </div>
      </div>
    );
  }

  if (!access.allowed) {
    return <ClosedProfilePage access={access} viewer={viewer} />;
  }

  return <>{children(access)}</>;
};

export default ProfileAccessBoundary;
